import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { AttachmentThumbnail } from '../../../components/attachments/AttachmentThumbnail';
import { type SavedAttachment } from '../../../services/attachmentService';
import { isCarouselSupported } from '../../../utils/attachmentCarousel';
import { summaryRowDisplayParts } from '../../../utils/summaryRowDisplayParts';

interface SummaryAttachmentRowProps {
  attachment: SavedAttachment;
  caption: string;
  testID?: string;
  isLastRow?: boolean;
  onOpen: (attachment: SavedAttachment) => void;
}

export const SummaryAttachmentRow: React.FC<SummaryAttachmentRowProps> = ({
  attachment,
  caption,
  testID,
  isLastRow = false,
  onOpen,
}) => {
  const label = attachment.attachmentName || 'Document';
  const { displayText, isSpecified } = summaryRowDisplayParts(caption);
  const canOpen = isCarouselSupported(attachment.mimeType);

  const handleOpen = () => {
    if (canOpen) {
      onOpen(attachment);
    }
  };

  return (
    <View style={[styles.row, isLastRow && styles.rowLast]} testID={testID}>
      <TouchableOpacity
        style={styles.preview}
        onPress={handleOpen}
        disabled={!canOpen}
        accessibilityRole={canOpen ? 'button' : undefined}
        accessibilityLabel={canOpen ? `View ${label}` : undefined}
        testID={testID != null ? `${testID}-thumb` : undefined}
      >
        <AttachmentThumbnail attachment={attachment} size="xsmall" />
      </TouchableOpacity>
      <View style={styles.labelContainer}>
        <Text style={styles.label}>{label}:</Text>
      </View>
      <View style={styles.valueContainer}>
        <Text
          style={isSpecified ? styles.value : styles.valueEmpty}
          testID={testID != null ? `${testID}-value` : undefined}
        >
          {displayText}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    alignItems: 'flex-start',
  },
  rowLast: {
    borderBottomWidth: 0,
  },
  preview: {
    marginRight: 8,
    marginTop: 2,
  },
  labelContainer: {
    flex: 1,
    flexBasis: 0,
    minWidth: 0,
    marginRight: 12,
    paddingRight: 4,
  },
  label: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  valueContainer: {
    flex: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  value: {
    fontSize: 14,
    color: '#333',
    lineHeight: 20,
  },
  valueEmpty: {
    fontSize: 14,
    color: '#999',
    lineHeight: 20,
    fontStyle: 'italic',
  },
});
